/* Modal — one dialog for the whole product.
 *
 * Radix handles what makes a dialog correct rather than merely visible: focus
 * moves in and is trapped, Escape closes, the page behind stops scrolling, and
 * the rest of the app is hidden from screen readers while it is open. All of
 * that is easy to get wrong by hand and invisible when you do.
 *
 * The header and footer stay put while the body scrolls, so a long composer
 * never pushes its own Post button off the bottom of a phone.
 */
import type { ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  children: ReactNode
  /** Pinned below the body — actions belong where they cannot scroll away. */
  footer?: ReactNode
  /** Defaults to 520px, the width the composer wants. */
  width?: string
  /** Set when closing mid-edit would lose work; see `onInteractOutside`. */
  guardClose?: boolean
}

export function Modal({
  open,
  onOpenChange,
  title,
  children,
  footer,
  width = '520px',
  guardClose = false,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-overlay-in" />

        <Dialog.Content
          style={{ width }}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 max-h-[90vh] max-w-[calc(100vw-24px)]',
            '-translate-x-1/2 -translate-y-1/2',
            'flex flex-col overflow-hidden rounded-lg bg-surface shadow-3',
            // modal-in, not fade-in: fade-in animates `transform`, which
            // overwrites the -translate-*-1/2 pair that centres this. The
            // dialog appeared bottom-right and snapped to the middle.
            'data-[state=open]:animate-modal-in',
          )}
          // A stray click on the backdrop must not throw away half-written
          // text. When the caller says there is work in progress, only the
          // explicit close paths apply.
          onInteractOutside={(e) => {
            if (guardClose) e.preventDefault()
          }}
          onEscapeKeyDown={(e) => {
            if (guardClose) e.preventDefault()
          }}
        >
          <header className="relative flex items-center justify-center border-b border-line-2 px-s6 py-s3">
            <Dialog.Title className="text-lg font-bold">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="absolute right-s3 grid h-9 w-9 place-items-center rounded-pill bg-surface-2 text-ink-2 transition-colors hover:bg-hover-2"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </header>

          {/* The only part that scrolls. */}
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

          {footer ? <div className="p-s4 pt-0">{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
